import { useEffect, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, MessageCircle, Globe, Lock, X } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
// Reuse the exact same placeholder catalog the vendor SMS Templates
// editor exposes — one source of truth so both sides insert the same
// tokens (they share the renderTemplate resolver at send-time).
import { VARIABLE_GROUPS } from '@/pages/SmsTemplatesPage';

// ── Super Admin: System SMS Templates ──────────────────────────────────
//
// Mirrors SuperAdminEmailTemplatesPage. Simpler than email because SMS is
// plain text (single `body` field, no block builder). CRUD is inline in
// a lightweight modal instead of a full-screen editor.
//
// Vendors see isPublic=true rows on their /sms-templates page via
// SmsTemplateService.list; editing on the vendor side triggers a
// copy-on-write clone into the tenant's SmsTemplate table.

interface SystemSmsTemplate {
  id: string;
  name: string;
  body: string;
  category?: string | null;
  isActive: boolean;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

// 160 GSM-7 chars per single SMS segment.
function segments(len: number): number {
  if (len <= 160) return 1;
  return Math.ceil(len / 153);
}

export function SuperAdminSmsTemplatesPage() {
  const [templates, setTemplates] = useState<SystemSmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SystemSmsTemplate | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SystemSmsTemplate | null>(null);

  const load = () => {
    setLoading(true);
    saApi
      .get('/super-admin/sms-templates')
      .then(({ data }) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await saApi.delete(`/super-admin/sms-templates/${confirmDelete.id}`);
      toast('success', 'System template deleted');
      setConfirmDelete(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete');
    }
  };

  // Flip isPublic inline — one click to publish or pull back. Optimistic
  // UI keeps the flip instant; reverts on failure so the badge doesn't lie.
  const togglePublic = async (t: SystemSmsTemplate) => {
    const next = !t.isPublic;
    setTemplates((prev) => prev.map((r) => (r.id === t.id ? { ...r, isPublic: next } : r)));
    try {
      await saApi.patch(`/super-admin/sms-templates/${t.id}`, { isPublic: next });
      toast('success', next ? 'Published to vendors' : 'Marked as internal');
    } catch (err: any) {
      setTemplates((prev) => prev.map((r) => (r.id === t.id ? { ...r, isPublic: t.isPublic } : r)));
      toast('error', err?.response?.data?.message || 'Failed to update visibility');
    }
  };

  return (
    <SuperAdminLayout title="SMS Templates">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#6366F1]">System SMS Templates</h2>
            <p className="text-sm text-[#64748B] mt-1">
              Platform-wide SMS defaults. Vendors see the Public ones on their SMS Templates page — they can use them as-is or Customize (clone into their own).
            </p>
          </div>
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg"
          >
            <Plus className="h-4 w-4" /> New Template
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-100 rounded-xl">
            <MessageCircle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-3">No system SMS templates yet</p>
            <button
              onClick={() => setEditing('new')}
              className="px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg"
            >
              Create Your First Default
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div
                key={t.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group flex flex-col"
              >
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-slate-800 truncate">{t.name}</h3>
                      {t.category && (
                        <span className="inline-block mt-1 text-[10px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                          {t.category}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {t.isPublic ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">
                          <Globe className="h-3 w-3" /> Public
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded">
                          <Lock className="h-3 w-3" /> Internal
                        </span>
                      )}
                      {!t.isActive && (
                        <span className="text-[10px] font-semibold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="flex-1 text-xs text-slate-600 whitespace-pre-wrap line-clamp-4">{t.body}</p>
                  <div className="flex items-center justify-between border-t border-slate-50 pt-2 mt-3">
                    <span className="text-[10px] text-slate-400">
                      {t.body.length} chars · {segments(t.body.length)} SMS
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => togglePublic(t)}
                        className={t.isPublic ? 'text-emerald-500 hover:text-emerald-700' : 'text-slate-400 hover:text-emerald-500'}
                        title={t.isPublic ? 'Currently visible to vendors — click to make internal' : 'Currently internal — click to publish to all vendors'}
                      >
                        {t.isPublic ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                      </button>
                      <button onClick={() => setEditing(t)} className="text-slate-400 hover:text-[#6366F1]" title="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setConfirmDelete(t)} className="text-slate-400 hover:text-red-500" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <SmsTemplateModal
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete system SMS template?"
        message="Tenants that have already cloned this template will keep their copy. Tenants who haven't will stop seeing this default."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </SuperAdminLayout>
  );
}

// ── Inline modal — plain text editor with placeholder + visibility fields.
function SmsTemplateModal({
  template,
  onClose,
  onSaved,
}: {
  template: SystemSmsTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(template?.name ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [category, setCategory] = useState(template?.category ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [isPublic, setIsPublic] = useState(template?.isPublic ?? false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Insert a placeholder token at the current caret position (or append
  // if the textarea isn't focused). Same helper as the vendor page so
  // authoring feels identical on both sides.
  const insertVar = (token: string) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async () => {
    if (!name.trim() || !body.trim()) {
      toast('error', 'Name and body are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), body, category: category || undefined, isActive, isPublic };
      if (template) {
        await saApi.patch(`/super-admin/sms-templates/${template.id}`, payload);
        toast('success', 'System template updated');
      } else {
        await saApi.post('/super-admin/sms-templates', payload);
        toast('success', 'System template created');
      }
      onSaved();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Right-side drawer — project convention (per feedback_patterns memory:
  // all drawers open from the right, not centered modals).
  return (
    <div className="fixed inset-0 z-[100] flex justify-end bg-black/30">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-in slide-in-from-right">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 flex-shrink-0">
          <h3 className="text-base font-bold text-slate-900">
            {template ? 'Edit SMS template' : 'New SMS template'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 rounded p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Template name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Booking Confirmation"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
            />
          </div>

          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Booking, Follow-up"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
            />
          </div>

          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">
              Message *
            </label>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="Hi {{lead.name}}, thanks for reaching out to {{orgName}}."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 resize-none"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              {body.length} chars · {segments(body.length)} SMS segment(s)
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-normal text-slate-600">Insert variable</label>
            <div className="space-y-2">
              {VARIABLE_GROUPS.map((g) => (
                <div key={g.label}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{g.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {g.items.map((v) => (
                      <button
                        key={v.token}
                        type="button"
                        onClick={() => insertVar(v.token)}
                        className="rounded-full border border-indigo-100 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-[#6366F1] hover:bg-indigo-100"
                        title={v.token}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-slate-300 text-[#6366F1]"
              />
              <span className="text-sm text-slate-700">Active</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="rounded border-slate-300 text-[#6366F1]"
              />
              <span className="text-sm text-slate-700">
                Public <span className="text-slate-400">(visible to all vendors)</span>
              </span>
            </label>
          </div>
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !name.trim() || !body.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white shadow-sm"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {template ? 'Save changes' : 'Create template'}
          </button>
        </div>
      </div>
    </div>
  );
}
